import { useLocale } from "../i18n/useLocale";

/**
 * DashboardShowcaseV2 — the admin dashboard on the home page.
 *
 * Was a scroll-driven assembly: the board was rebuilt in DOM and its panels flew
 * in from outside the frame as the section scrolled past. Replaced with the real
 * product screenshot — it is the actual interface rather than an approximation
 * of it, and it costs nothing to scroll past.
 *
 * Screenshots are cropped on delivery (`c_crop`) to drop the white margin at the
 * top and the row of half-cut glyphs at the bottom, then re-encoded and sized
 * (`f_auto,q_auto,w_1600`) — the originals are ~4110px wide, ~600KB each.
 *
 * The app UI ships in Dutch, French and English only, so every locale outside
 * nl / fr-be gets the English board.
 */
const BOARD_BASE = "https://res.cloudinary.com/lbx0aw1r/image/upload";
const BOARDS = {
  nl: `${BOARD_BASE}/c_crop,y_12,h_2225/f_auto,q_auto,w_1600/Dashboard_dutch_nqtv5r.png`,
  "fr-be": `${BOARD_BASE}/c_crop,y_8,h_2225/f_auto,q_auto,w_1600/dashboard_french_tbqx4k.png`,
  en: `${BOARD_BASE}/c_crop,y_10,h_2229/f_auto,q_auto,w_1600/dashboard_English_knv08d.png`,
} as const;

export default function DashboardShowcaseV2() {
  const { locale, t } = useLocale();
  const board = BOARDS[locale as keyof typeof BOARDS] ?? BOARDS.en;

  // Desktop-only section — the board needs width; on mobile we go hero → bento.
  return (
    <section className="hidden overflow-hidden font-ds leading-[1.24] lg:block lg:pt-[100px] lg:pb-[60px]">
      <div className="mx-auto max-w-content px-4 md:px-10">
        {/* width/height match the delivered crop (1600 x 867) so the row reserves
            its space before the image lands — no layout shift. */}
        <img
          src={board}
          alt={t("home:dashboard.alt")}
          width={1600}
          height={867}
          loading="lazy"
          decoding="async"
          className="w-full rounded-3xl ring-1 ring-black/5"
        />
      </div>
    </section>
  );
}
